import { Form } from '@/common/form/Form';
import { FormFields } from '@/common/form/rendering/FormFields';
import { useRsrc } from '@/common/rsrc/RsrcContext';
import { Box, Button, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

/**
 * GP-9: This is the final step in the Calc Job Dialog Wizard
 * The user names and selects the cutoff instant for the calculation before running it.
 */
export const OptSelectStep = ({ calcChainReq, setCalcChainReq, setNextStep }) => {
  const rsrc = useRsrc();

  const nowInstant = useMemo(() => new Date().toISOString(), []);

  const [currJobIndex, setCurrJobIndex] = useState(0);
  const [estCalcTypeOptions, setEstCalcTypeOptions] = useState([]);

  const jobReqsList = calcChainReq.calcJobReqs;

  const liabPolType = calcChainReq.liabPolType;
  const currJobReq = useMemo(() => jobReqsList[currJobIndex], [jobReqsList, currJobIndex]);
  const isRepro = calcChainReq.calcJobReqs[0].src !== 'PER';
  const isBp = liabPolType.code === 'BP';

  const [currCalcComplete, setCurrCalcComplete] = useState(false);

  // On init
  useEffect(() => {
    if (isBp) {
      const rowsReq = { enumCode: 'CALC_TYPE', filterMethod: 'estCalcTypes' };
      rsrc.data('gp', 'ENUM_LIST_DATA_PLUGIN', rowsReq).then((types) => {
        setEstCalcTypeOptions(types || []);
      });
    }
  }, []);

  // Set defaults
  const [formValues, setFormValues] = useState({});

  // When formValues change, update the current job in the chain
  useEffect(() => {
    setCalcChainReq((prev) => {
      const newReqs = [...prev.calcJobReqs];
      const updatedReq = {
        ...newReqs[currJobIndex],
        ...formValues,
      };

      newReqs[currJobIndex] = updatedReq;

      // Check if new values allow user to move forward
      setCurrCalcComplete(updatedReq.name && updatedReq.cutoffInstant && (isBp ? updatedReq.estCalcType : true));
      const allComplete = newReqs.every((jr) => jr.name && jr.cutoffInstant && (isBp ? jr.estCalcType : true));

      setNextStep(allComplete ? 'Run' : null);

      return {
        ...prev,
        calcJobReqs: newReqs,
      };
    });
  }, [formValues]);

  // When currJobIndex changes, update formValues to match the current job (or its defaults)
  useEffect(() => {
    const { name, cutoffInstant, estCalcType, period, wbkName } = jobReqsList[currJobIndex];
    let finalName = name || (isRepro ? `${wbkName} Reproduction` : `${liabPolType.displayName} ${period.code}`);
    const defaults = {
      name: finalName,
      cutoffInstant: cutoffInstant || nowInstant,
      ...(isBp ? { estCalcType: estCalcType ?? 'EST_BPI' } : {}),
    };
    setFormValues(defaults);
  }, [currJobIndex]);

  const fieldDefs = useMemo(() => {
    const { name, cutoffInstant, estCalcType, period, wbkName } = currJobReq;
    let finalName = name || (isRepro ? `${wbkName} Reproduction` : `${liabPolType.displayName} ${period.code}`);
    const defaults = {
      name: finalName,
      cutoffInstant: cutoffInstant || nowInstant,
      ...(isBp ? { estCalcType: estCalcType ?? 'EST_BPI' } : {}),
    };

    const fieldDefs = [
      {
        field: 'name',
        name: 'name',
        displayName: 'Name',
        type: 'STRING',
        editLevel: 'ALWAYS',
        required: true,
        defaultValue: defaults.name,
      },
      {
        field: 'cutoffInstant',
        name: 'cutoffInstant',
        displayName: 'Cutoff Instant',
        type: 'INSTANT',
        editLevel: 'ALWAYS',
        required: true,
        defaultValue: defaults.cutoffInstant,
        hint: 'All data within the calculation will be taken as it existed at the specified point in time. If left blank, the system will use the instant the Calculation is started.',
      },
    ];

    if (isBp && estCalcTypeOptions) {
      fieldDefs.push({
        field: 'estCalcType',
        name: 'estCalcType',
        displayName: 'Estimation Calculation Type',
        type: 'ENUM',
        enumCode: 'CALC_TYPE',
        enumMethod: 'estCalcTypes',
        defaultValue: defaults.estCalcType,
        values: estCalcTypeOptions,
        editLevel: 'ALWAYS',
        required: true,
        hint: 'This option controls the Estimations that will be performed before the Best Price Calculation.',
      });
    }

    return fieldDefs;
  }, [currJobIndex, estCalcTypeOptions]);

  const subtitle =
    jobReqsList.length === 1 ?
      `Provide a name and data cutoff instant for your calculation. Once complete, click "Run" to start the calculation.`
    : `Provide a name and data cutoff instant for your ${currJobReq?.period.code} calculation. Once complete, click "Run" to start the calculations (${currJobIndex + 1} of ${jobReqsList.length}).`;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-end',
          my: 1,
        }}
      >
        <Typography variant="h6">{subtitle}</Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {jobReqsList.length > 1 && (
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => setCurrJobIndex(currJobIndex - 1)}
                disabled={currJobIndex === 0}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Previous Calculation
              </Button>
              <Button
                variant="outlined"
                disabled={currJobIndex === jobReqsList.length - 1 || !currCalcComplete}
                onClick={() => setCurrJobIndex(currJobIndex + 1)}
                sx={{ whiteSpace: 'nowrap' }}
              >
                Next Calculation
              </Button>
            </Box>
          )}
        </Box>
      </Box>

      {/* Form */}
      <Box sx={{ flex: 1, minHeight: 10, overflow: 'auto', width: '100%' }}>
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 2 }}>
          <Form formValues={formValues} setFormValues={setFormValues}>
            <FormFields fieldDefs={fieldDefs} />
          </Form>
        </Box>
      </Box>
    </Box>
  );
};
